// /src/hooks/useAuctionActions.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { useTracking } from "react-tracking";

import {
	createPackage,
	updatePackage,
	updatePackageStatus,
	updatePackageItem,
	updatePackageBidder,
} from "../slices/auctionSlice";

import { useDialogBox } from "../hooks/useDialogBox";
import { debugLog, errorLog } from "../utils/debugUtils";
import { Session } from "../utils/sessionUtils";

/**
 * All auction CRUD and confirmation flows.
 * Accepts callbacks and state setters from the main component.
 */
export function useAuctionActions({
	fetchAuctionPackageByIdOrUuid,
	fetchAuctionPackages,
	allUsers,
	clearDataError,
	handleBackToList,
	localAuctionPackage,
	selectedPkg,
	setLocalAuctionPackage,
	auctionItem,
	setAuctionItem,
}) {
	const dispatch = useDispatch();
	const { trackEvent } = useTracking();
	const { showDialog, withError } = useDialogBox();

	// CREATE Auction Package
	const createAuctionPackageAction = useCallback(() => {
		debugLog(3, "[useAuctionActions][ACTION] createAuctionPackageAction", {
			localAuctionPackage: localAuctionPackage,
		});
		debugLog(2, "[useAuctionActions][FLOW] createPackage started", {});

		trackPromise(
			dispatch(createPackage({ data: localAuctionPackage }))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useAuctionActions][STATE] auction package created", { response: response });

					showDialog(
						{
							title: "Successful",
							message: ["The Auction Package was created successfully!"],
						},
						() => {
							fetchAuctionPackageByIdOrUuid &&
								fetchAuctionPackageByIdOrUuid(response.id, allUsers);
							handleBackToList && handleBackToList();
							clearDataError && clearDataError();
						}
					);
					trackEvent({
						event: "createAuctionPackage",
						props: { to: localAuctionPackage },
					});
				})
				.catch((error) => {
					errorLog(2, "[useAuctionActions][FLOW] createPackage failed", { error: error });
					console.error("createAuctionPackage failed", error);
					withError(error, null, { fallbackMessage: "Package create failed." });
				})
		);
	}, [
		dispatch,
		localAuctionPackage,
		fetchAuctionPackageByIdOrUuid,
		handleBackToList,
		clearDataError,
		showDialog,
		withError,
		trackEvent,
		allUsers,
	]);

	// UPDATE Auction Package
	const updateAuctionPackageAction = useCallback(() => {
		debugLog(3, "[useAuctionActions][ACTION] updateAuctionPackageAction", {
			localAuctionPackage: localAuctionPackage,
		});
		debugLog(2, "[useAuctionActions][FLOW] updatePackage started", { id: localAuctionPackage?.id });

		trackPromise(
			dispatch(updatePackage({ id: localAuctionPackage.id, data: localAuctionPackage }))
				.unwrap()
				.then((response) => {
					showDialog(
						{
							title: "Successful",
							message: ["The Auction Package was updated successfully!"],
						},
						() => {
							fetchAuctionPackageByIdOrUuid &&
								fetchAuctionPackageByIdOrUuid(localAuctionPackage.id, allUsers);
							clearDataError && clearDataError();
						}
					);
					trackEvent({
						event: "updateAuctionPackage",
						props: { to: localAuctionPackage },
					});
				})
				.catch((error) => {
					errorLog(2, "[useAuctionActions][FLOW] updatePackage failed", {
						id: localAuctionPackage?.id,
						error: error,
					});
					console.error("updateAuctionPackage failed", error);
					withError(error, null, { fallbackMessage: "Package update failed." });
				})
		);
	}, [
		dispatch,
		localAuctionPackage,
		fetchAuctionPackageByIdOrUuid,
		clearDataError,
		showDialog,
		withError,
		trackEvent,
		allUsers,
	]);

	// UPDATE Auction Package Status (archive, restore, delete, etc.)
	const updateAuctionPackageStatusAction = useCallback(
		(id, newStatus, showMsgBox = true) => {
			debugLog(3, "[useAuctionActions][ACTION] updateAuctionPackageStatusAction", {
				id,
				newStatus,
				showMsgBox,
			});
			debugLog(2, "[useAuctionActions][FLOW] updatePackageStatus started", {
				id: id,
				newStatus: newStatus,
			});

			let strAction = "archived";
			if (newStatus === 1) strAction = "re-activated";
			else if (newStatus === -1) strAction = "deleted";

			trackPromise(
				dispatch(
					updatePackageStatus({
						id,
						status_id: newStatus,
						updatedBy: Session().currUser.id,
					})
				)
					.unwrap()
					.then(() => {
						debugLog(3, "[useAuctionActions][STATE] auction package status updated", {
							id,
							newStatus,
							action: strAction,
						});

						if (showMsgBox) {
							showDialog(
								{
									title: "Successful",
									message: [
										`The Auction Package "${localAuctionPackage?.name}" was ${strAction} successfully!`,
									],
								},
								() => {
									if (newStatus === 1)
										fetchAuctionPackageByIdOrUuid &&
											fetchAuctionPackageByIdOrUuid(id, allUsers);
									else handleBackToList && handleBackToList();
								}
							);
						} else {
							if (newStatus === 1)
								fetchAuctionPackageByIdOrUuid && fetchAuctionPackageByIdOrUuid(id, allUsers);
							else handleBackToList && handleBackToList();
						}
						trackEvent({
							event: `Auction Package (#${id}) was ${strAction}`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useAuctionActions][FLOW] updatePackageStatus failed", {
							id: id,
							newStatus: newStatus,
							error: error,
						});
						console.error("updateAuctionStatus failed", error);
						withError(error, null, { fallbackMessage: "Package status update failed." });
					})
			);
		},
		[
			dispatch,
			localAuctionPackage,
			showDialog,
			withError,
			fetchAuctionPackageByIdOrUuid,
			handleBackToList,
			trackEvent,
			allUsers,
		]
	);

	/**
	 * Update a single Package Item
	 * Mirrors Baseline's `updateItemAction` pattern used by BaselineItemConfigs.jsx.
	 * Re-read the parent package after confirmation so server-derived item fields
	 * are reflected in the open Auction view without changing the save workflow.
	 */
	const updatePackageItemAction = useCallback(() => {
		debugLog(3, "[useAuctionActions][ACTION] updatePackageItemAction", { auctionItem: auctionItem });

		if (!auctionItem?.id) {
			showDialog({
				title: "Error",
				message: ["Missing package item id."],
				type: "error",
			});
			return;
		}

		// Include the same audit metadata used by the existing Auction item update path.
		const itemForSave = {
			...auctionItem,
			updatedAt: new Date(),
			updatedBy: Session().currUser?.id,
		};
		debugLog(2, "[useAuctionActions][FLOW] updatePackageItem started", { id: itemForSave?.id });

		trackPromise(
			dispatch(updatePackageItem({ id: itemForSave.id, data: itemForSave }))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useAuctionActions][STATE] package item updated", {
						response,
						to: itemForSave,
					});

					// Keep the current item view responsive while the parent reload follows acknowledgment.
					setAuctionItem && setAuctionItem(itemForSave);

					showDialog(
						{
							title: "Successful",
							message: ["The package item was updated successfully!"],
						},
						() => {
							fetchAuctionPackageByIdOrUuid &&
								fetchAuctionPackageByIdOrUuid(itemForSave.package_id, allUsers);
							clearDataError && clearDataError();
						}
					);

					trackEvent({
						event: `updatePackageItem-${itemForSave.id}`,
						props: { to: itemForSave },
					});
				})
				.catch((error) => {
					errorLog(2, "[useAuctionActions][FLOW] updatePackageItem failed", {
						id: itemForSave?.id,
						error: error,
					});
					console.error("updatePackageItem failed", error);
					withError(error, null, { fallbackMessage: "Package item update failed." });
				})
		);
	}, [
		dispatch,
		auctionItem,
		setAuctionItem,
		showDialog,
		withError,
		clearDataError,
		fetchAuctionPackageByIdOrUuid,
		allUsers,
		trackEvent,
	]);

	// UPDATE Package Bidder
	const updatePackageBidderAction = useCallback(
		({ currBidder, setCurrBidder }) => {
			debugLog(3, "[useAuctionActions][ACTION] updatePackageBidderAction", {
				currBidder: currBidder,
			});

			const cBidder = { ...currBidder };
			setCurrBidder && setCurrBidder(null);
			debugLog(2, "[useAuctionActions][FLOW] updatePackageBidder started", {
				id: currBidder?.id,
			});

			trackPromise(
				dispatch(
					updatePackageBidder({
						id: currBidder.id,
						data: currBidder,
					})
				)
					.unwrap()
					.then((response) => {
						debugLog(3, "[useAuctionActions][STATE] package bidder updated", {
							response: response,
							cBidder: cBidder,
						});

						if (setLocalAuctionPackage && localAuctionPackage) {
							const newBidders = (localAuctionPackage.auc_package_bidders || []).map((bidder) =>
								bidder.id === cBidder.id ? cBidder : bidder
							);
							setLocalAuctionPackage({
								...localAuctionPackage,
								auc_package_bidders: newBidders,
							});
						}

						showDialog(
							{
								title: "Successful",
								message: ["The package Bidder was updated successfully!"],
							},
							() => {
								setCurrBidder && setCurrBidder(null);
								fetchAuctionPackageByIdOrUuid &&
									fetchAuctionPackageByIdOrUuid(localAuctionPackage.id, allUsers);
							}
						);
						trackEvent({
							event: "updatePackageBidder",
							props: { to: cBidder },
						});
					})
					.catch((error) => {
						errorLog(2, "[useAuctionActions][FLOW] updatePackageBidder failed", {
							id: currBidder?.id,
							error: error,
						});
						console.error("updatePackageBidder failed", error);
						withError(error, null, { fallbackMessage: "Package bidder update failed." });
					})
			);
		},
		[
			dispatch,
			showDialog,
			withError,
			trackEvent,
			setLocalAuctionPackage,
			localAuctionPackage,
			fetchAuctionPackageByIdOrUuid,
			allUsers,
		]
	);

	return {
		createAuctionPackageAction,
		updateAuctionPackageAction,
		updateAuctionPackageStatusAction,
		updatePackageBidderAction,
		updatePackageItemAction,
	};
}
